import { useCallback, useEffect, useState } from "react";

/**
 * The value a closing dialog should keep rendering.
 *
 * A dialog closes before it unmounts, and its content must not blank out while
 * the exit animation runs, so the last non-null value is held past the close.
 *
 * The live value always wins while there is one. Holding it for a render longer
 * — the shape a plain `useState` plus a syncing effect gives — opens the dialog
 * on the target before it and leaves every gate derived from it reading the
 * wrong row for that frame, which is how a dialog ends up unmounted while open.
 *
 * `clearHeld` drops the held value for a caller that must not keep rendering
 * the last one, such as one whose fetch failed.
 */
export const useHeldWhileClosing = <T>(value: T | null) => {
  const [heldValue, setHeldValue] = useState(value);

  useEffect(() => {
    if (value) setHeldValue(value);
  }, [value]);

  const clearHeld = useCallback(() => setHeldValue(null), []);

  return [value ?? heldValue, clearHeld] as const;
};
